/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

// EXCLUDED CLIENT TYPES (v1_831). Policy questionnaire section 1 lets the firm
// mark a client type excluded; the signed version writes the keys to
// risk_config.excludedClientTypes. When a case is one of them, the risk engine
// opens the clientTypeExcluded gate (action Decline), rates the relevant
// category High, and the tier cannot be adjusted below High, exactly as a
// prohibited jurisdiction does. Nothing blocks approval because a gate fired.
//
// How each type is detected (decisions 26/09/26):
//   crypto_businesses  the pinned entity type is VASP. Never "the relationship
//                      involves crypto" or a crypto mention.
//   pep_connected      pepPresent: an identity check PEP match or a screening
//                      match the officer confirmed. Never an unconfirmed
//                      potential match.
//   ceps               the opaque ownership flag (trust, foundation, nominee,
//                      bearer shares; not dismissed by the officer), or two or
//                      more corporate layers between the applicant and a
//                      natural person, or a corporate holder at or above the
//                      ownership threshold whose own owners are unresolved.
//   retail             v1_832: the legal form is individual (a natural person
//                      client), never the FinSA classification.
//   unincorporated     v1_832: sole trader, ordinary partnership or
//                      unincorporated association.
//   trusts_charities   v1_832: trust, foundation or charity, or a charity
//                      registration number on the file.
// The legal form comes from src/shared/legal-form.js. Pure.

import { reconcileOwnership } from './ownership-reconcile.js';
import { GATE_SOURCES } from './legal-form.js';

export const CLIENT_TYPE_LABELS = Object.freeze({
  retail: 'Retail clients',
  unincorporated: 'Unincorporated businesses',
  trusts_charities: 'Trusts and charities',
  ceps: 'Complex or opaque ownership structures',
  pep_connected: 'Clients connected to a politically exposed person',
  crypto_businesses: 'Crypto businesses',
});
// v1_832: all six, the last three from the client's legal form.
export const ENFORCED_CLIENT_TYPES = Object.freeze(['crypto_businesses', 'pep_connected', 'ceps', 'retail', 'unincorporated', 'trusts_charities']);
export const NEXT_CLIENT_TYPES = Object.freeze([]);
// The category the engine rates High for each type.
export const CLIENT_TYPE_CATEGORY = Object.freeze({ crypto_businesses: 'sector', pep_connected: 'customer', ceps: 'customer', retail: 'customer', unincorporated: 'customer', trusts_charities: 'customer' });
// v1_832: the legal forms each of the last three types covers.
const UNINCORPORATED_FORMS = ['sole_trader', 'ordinary_partnership', 'unincorporated_association'];
const TRUST_FORMS = ['trust', 'foundation', 'charity'];

// The excluded keys from a firm risk configuration (any of the six; unknown
// keys dropped).
export function excludedClientTypes(riskConfig) {
  const v = riskConfig && riskConfig.excludedClientTypes;
  return Array.isArray(v) ? [...new Set(v.filter(k => Object.prototype.hasOwnProperty.call(CLIENT_TYPE_LABELS, k)))] : [];
}

// Layers: a natural person holder whose shortest path to the applicant passes
// through two or more corporate entities (depth counts links: person, one
// company, applicant is 2).
function cepsSignal({ ownership, report, registerExtracts, threshold }) {
  if (ownership && ownership.opaque === true) {
    const ev = ownership.opaqueEvidence || {};
    return { detected: true, basis: `opaque ownership vehicle on the file (${ev.term || 'trust, foundation, nominee or bearer shares'}${ev.text ? ': ' + String(ev.text).slice(0, 80) : ''})` };
  }
  let rec = null;
  try { rec = reconcileOwnership(report || {}, registerExtracts || [], { threshold }); } catch (_) { rec = null; }
  const parties = (rec && rec.resolution && Array.isArray(rec.resolution.parties)) ? rec.resolution.parties : [];
  const deep = parties.filter(p => p && !p.corporate && (p.depth || 1) >= 3);
  if (deep.length) return { detected: true, basis: `${deep[0].name} holds through ${(deep[0].depth || 3) - 1} corporate layers` };
  const viaUbos = ((report && report.ubos) || []).find(u => u && u.corporateUBO !== true && Array.isArray(u.intermediaries) && u.intermediaries.length >= 2);
  if (viaUbos) return { detected: true, basis: `${viaUbos.name} holds through ${viaUbos.intermediaries.length} corporate layers (${viaUbos.intermediaries.join(', ')})` };
  // Unresolved: a corporate holder at or above the threshold with no holder of
  // its own on the file (no leg names it as the entity held). The resolution's
  // own frontier also counts a holder whose owners are known but not yet looked
  // up by the officer, so it is not used here.
  const norm = (x) => String(x || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const legs = [...((rec && rec.ownershipChain) || []), ...(((report && report.ownershipChain) || []))];
  const held = new Set(legs.map(l => norm(l && (l.held || l.holds))).filter(Boolean));
  const open = parties.filter(p => p && p.corporate && !p.resolved && !p.uncertain && Number.isFinite(Number(p.effective)) && Number(p.effective) >= threshold && !held.has(norm(p.name)));
  if (open.length) return { detected: true, basis: `${open[0].name} holds ${open[0].effective} percent and no holder of its own is on the file` };
  return { detected: false, basis: null };
}

// What the case is, for the three types detected today.
export function clientTypeSignals({ entityType, pepPresent, ownership, report, registerExtracts, threshold = 25, legalForm = null } = {}) {
  const lf = legalForm && legalForm.form ? legalForm : { form: 'company', basis: 'no legal form on the case' };
  const lfBasis = `the client's legal form is ${String(lf.label || lf.form).toLowerCase()} (${lf.basis})`;
  // v1_832 change 3: these three open only on a legal form the officer, the
  // invitation or the documents gave. The name backstop still places the case in
  // its floor and threshold group, but never declines a client alone; the signal
  // then says what is held back (nameOnly) so the case can show it.
  const firm = GATE_SOURCES.includes(lf.source);
  const sig = (hit, basis) => hit
    ? (firm ? { detected: true, basis } : { detected: false, basis: null, nameOnly: basis })
    : { detected: false, basis: null };
  return {
    retail: sig(lf.form === 'individual', lfBasis),
    unincorporated: sig(UNINCORPORATED_FORMS.includes(lf.form), lfBasis),
    trusts_charities: sig(TRUST_FORMS.includes(lf.form) || !!lf.charityNumber, TRUST_FORMS.includes(lf.form) ? lfBasis : `a charity registration number is on the file (${lf.charityNumber})`),
    crypto_businesses: String(entityType || '').toUpperCase() === 'VASP'
      ? { detected: true, basis: 'the entity type is VASP (crypto or digital asset business)' }
      : { detected: false, basis: null },
    pep_connected: pepPresent === true
      ? { detected: true, basis: 'a politically exposed person match from identity verification, or a screening match the officer confirmed' }
      : { detected: false, basis: null },
    ceps: cepsSignal({ ownership, report, registerExtracts, threshold }),
  };
}

// ENHANCED DUE DILIGENCE CLIENT TYPES (v1_848). A section 1 EDD answer for one
// of these four types rates the case's category High, with the firm's answer as
// the basis, and fires the clientTypeEdd override (a High floor, EDD), detected
// exactly as the Excluded answer is. Crypto businesses take EDD through the VASP
// sector rating (sectorRisk, v1_843), and a PEP connection is EDD by law (the
// pepPresent override), so those two are not here. Until v1_848 an EDD answer
// for these four was recorded on the signed policy and acted on nothing.
export const EDD_FLOOR_CLIENT_TYPES = Object.freeze(['retail', 'unincorporated', 'trusts_charities', 'ceps']);
export function eddClientTypes(riskConfig) {
  const v = riskConfig && riskConfig.eddClientTypes;
  return Array.isArray(v) ? [...new Set(v.filter(k => EDD_FLOOR_CLIENT_TYPES.includes(k)))] : [];
}
// The hits: answered EDD by the firm and detected on the case. A type the firm
// also excluded is the gate's (Decline), never both.
export function eddClientTypeHits(signals, edd, excluded = []) {
  const e = Array.isArray(edd) ? edd : [], x = Array.isArray(excluded) ? excluded : [];
  return EDD_FLOOR_CLIENT_TYPES
    .filter(k => e.includes(k) && !x.includes(k) && signals && signals[k] && signals[k].detected)
    .map(k => ({ key: k, label: CLIENT_TYPE_LABELS[k], category: CLIENT_TYPE_CATEGORY[k], basis: signals[k].basis }));
}

// The hits: excluded by the firm and detected on the case.
export function excludedClientTypeHits(signals, excluded) {
  const ex = Array.isArray(excluded) ? excluded : [];
  return ENFORCED_CLIENT_TYPES
    .filter(k => ex.includes(k) && signals && signals[k] && signals[k].detected)
    .map(k => ({ key: k, label: CLIENT_TYPE_LABELS[k], category: CLIENT_TYPE_CATEGORY[k], basis: signals[k].basis }));
}
